import type { GroupBy } from './workLogGroups';
import { israelToday } from './israelTime';

/** D-319 sec.9 - the tabs of "פירוט לוגים" (same ids as the server's LOG_DETAIL_TABS). */
export const LOG_DETAIL_TABS = ['service', 'bank', 'no_contract', 'projects', 'customers', 'employees', 'internal'] as const;
export type LogDetailTab = (typeof LOG_DETAIL_TABS)[number];

/** The rows cap of GET /log-detail; at the cap the page warns instead of cutting silently. */
export const LOG_DETAIL_LIMIT = 2000;

/** Same rule as the server's seesOthersWorkLogs: the OTE manager, or an account manager / project manager / team lead. */
export function seesOthersLogs(c: { isGeneralManager: boolean; managedOrgIds: string[]; managedProjectIds: string[]; teamLeadProjectIds: string[] }): boolean {
  return c.isGeneralManager || c.managedOrgIds.length > 0 || c.managedProjectIds.length > 0 || c.teamLeadProjectIds.length > 0;
}

/** The tabs a caller gets: "חוזי שירות" (the old monthly approvals, an OTE-manager screen) only for the OTE manager. */
export function tabsFor(isGeneralManager: boolean): readonly LogDetailTab[] {
  return isGeneralManager ? LOG_DETAIL_TABS : LOG_DETAIL_TABS.filter((t) => t !== 'service');
}

/** "חוזי שירות" is the OTE manager's default tab; anyone else starts at "פרויקטים ופיתוח". */
export function tabParam(v: string | null, isGeneralManager = true): LogDetailTab {
  const allowed = tabsFor(isGeneralManager);
  return (allowed as readonly string[]).includes(v ?? '') ? (v as LogDetailTab) : allowed[0] === 'service' ? 'service' : 'projects';
}

/** Default "קבץ לפי" of a tab: service / bank / customers by customer, projects by project, employees by employee. */
export function defaultGroupFor(tab: LogDetailTab): GroupBy {
  switch (tab) {
    case 'projects': return 'project';
    case 'employees': return 'worker';
    case 'internal': return 'worker';
    default: return 'customer';
  }
}

/** 'YYYY-MM' from the URL; anything else = the current Israeli month. */
export function monthParam(v: string | null): string {
  return v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v) ? v : israelToday().slice(0, 7);
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** The distinct {id, label} pairs of `key` in the rows, sorted by label; rows without the field are skipped. */
export function uniqueOptions<R extends object>(rows: R[], key: keyof R, label: (r: R) => string | null | undefined): { id: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const r of rows) {
    const id = r[key] as unknown as string | null | undefined;
    if (id && !seen.has(id)) seen.set(id, label(r) || id);
  }
  return [...seen].map(([id, l]) => ({ id, label: l })).sort((a, b) => a.label.localeCompare(b.label, 'he'));
}

/** The most months one range (and one PDF / CSV) may cover - the same cap as the server. */
export const LOG_DETAIL_MAX_MONTHS = 12;

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The number of calendar months from `from` to `to` ('YYYY-MM', inclusive); 0 when either is not a month. */
export function rangeMonthCount(from: string, to: string): number {
  if (!MONTH_RE.test(from) || !MONTH_RE.test(to)) return 0;
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return (ty - fy) * 12 + (tm - fm) + 1;
}

/** Why a month range cannot be used: 'invalid' (not months), 'order' (to before from), 'tooLong' (> 12 months), or null. */
export function rangeError(from: string, to: string): 'order' | 'tooLong' | 'invalid' | null {
  if (!MONTH_RE.test(from) || !MONTH_RE.test(to)) return 'invalid';
  const n = rangeMonthCount(from, to);
  if (n < 1) return 'order';
  return n > LOG_DETAIL_MAX_MONTHS ? 'tooLong' : null;
}

/** First day of `from` .. last day of `to`, as the bare dates the export / log-detail endpoints take. */
export function rangeBounds(from: string, to: string): { start_date: string; end_date: string } {
  const [y, m] = to.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { start_date: `${from}-01`, end_date: `${to}-${String(last).padStart(2, '0')}` };
}

/** The real (UTF-8) file name the server chose in Content-Disposition (`filename*=`), or null. */
export function fileNameFromDisposition(header: string | null | undefined): string | null {
  const m = /filename\*=UTF-8''([^;]+)/i.exec(header ?? '');
  if (!m) return null;
  try { return decodeURIComponent(m[1].trim()); } catch { return null; }
}
